/**
 * TanStack Query hooks — Carnet de contacts assureur (9.V.2, UI 9.V.2b).
 *
 * Routes API nested (9.V.2a) :
 *   GET/POST   /v1/insurers/:insurerId/contacts
 *   PATCH      /v1/insurers/:insurerId/contacts/:contactId
 *   POST       /v1/insurers/:insurerId/contacts/:contactId/{activate,deactivate}
 *   GET/POST   /v1/insurers/:insurerId/contacts/:contactId/emails
 *   PATCH      /v1/insurers/:insurerId/contacts/:contactId/emails/:emailId
 *   POST       /v1/insurers/:insurerId/contacts/:contactId/emails/:emailId/{activate,deactivate}
 *
 * Le statut passe par activate/deactivate (jamais PATCH) ; `is_primary` via
 * POST/PATCH (promotion transactionnelle côté serveur).
 */

import type { DbPage } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

type Status = "active" | "inactive";
export type StatusFilter = Status | "all";

export type InsurerContactRow = {
  id: string;
  tenant_id: string;
  insurer_id: string;
  full_name: string | null;
  role: string | null;
  department: string | null;
  is_primary: boolean;
  status: Status;
  created_at: string;
  updated_at: string;
};

export type InsurerContactEmailRow = {
  id: string;
  tenant_id: string;
  insurer_id: string;
  insurer_contact_id: string;
  email: string;
  label: string | null;
  is_primary: boolean;
  preferred_language: "fr" | "en" | null;
  status: Status;
  created_at: string;
  updated_at: string;
};

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------

const contactsKey = (insurerId: string) => ["insurer-contacts", insurerId];

export function useInsurerContacts(insurerId: string | undefined, status: StatusFilter = "all") {
  return useQuery({
    queryKey: [...contactsKey(insurerId ?? ""), { status }],
    queryFn: () =>
      api.get<DbPage<InsurerContactRow>>(
        `/v1/insurers/${insurerId}/contacts?status=${status}&limit=100`,
      ),
    enabled: !!insurerId,
    placeholderData: (prev) => prev,
  });
}

export function useCreateInsurerContact(insurerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<InsurerContactRow>(`/v1/insurers/${insurerId}/contacts`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKey(insurerId) }),
  });
}

export function useUpdateInsurerContact(insurerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { contactId: string; input: Record<string, unknown> }) =>
      api.patch<InsurerContactRow>(
        `/v1/insurers/${insurerId}/contacts/${args.contactId}`,
        args.input,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKey(insurerId) }),
  });
}

export function useSetInsurerContactStatus(insurerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { contactId: string; action: "activate" | "deactivate" }) =>
      api.post<InsurerContactRow>(
        `/v1/insurers/${insurerId}/contacts/${args.contactId}/${args.action}`,
        {},
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKey(insurerId) }),
  });
}

/**
 * Désactivation d'un contact avec remplaçant principal optionnel (9.V.2b) —
 * un SEUL appel, promotion transactionnelle côté serveur (aucun 2-PATCH).
 */
export function useDeactivateInsurerContact(insurerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { contactId: string; replacementContactId?: string | null }) =>
      api.post<{ deactivated: InsurerContactRow; new_primary: InsurerContactRow | null }>(
        `/v1/insurers/${insurerId}/contacts/${args.contactId}/deactivate`,
        args.replacementContactId ? { replacement_contact_id: args.replacementContactId } : {},
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contactsKey(insurerId) }),
  });
}

// ---------------------------------------------------------------------------
// Emails (nested sous un contact)
// ---------------------------------------------------------------------------

const emailsKey = (insurerId: string, contactId: string) => [
  "insurer-contact-emails",
  insurerId,
  contactId,
];

export function useInsurerContactEmails(
  insurerId: string,
  contactId: string,
  status: StatusFilter = "all",
) {
  return useQuery({
    queryKey: [...emailsKey(insurerId, contactId), { status }],
    queryFn: () =>
      api.get<DbPage<InsurerContactEmailRow>>(
        `/v1/insurers/${insurerId}/contacts/${contactId}/emails?status=${status}&limit=100`,
      ),
    placeholderData: (prev) => prev,
  });
}

export function useCreateInsurerContactEmail(insurerId: string, contactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api.post<InsurerContactEmailRow>(
        `/v1/insurers/${insurerId}/contacts/${contactId}/emails`,
        input,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: emailsKey(insurerId, contactId) }),
  });
}

export function useUpdateInsurerContactEmail(insurerId: string, contactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emailId: string; input: Record<string, unknown> }) =>
      api.patch<InsurerContactEmailRow>(
        `/v1/insurers/${insurerId}/contacts/${contactId}/emails/${args.emailId}`,
        args.input,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: emailsKey(insurerId, contactId) }),
  });
}

export function useSetInsurerContactEmailStatus(insurerId: string, contactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emailId: string; action: "activate" | "deactivate" }) =>
      api.post<InsurerContactEmailRow>(
        `/v1/insurers/${insurerId}/contacts/${contactId}/emails/${args.emailId}/${args.action}`,
        {},
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: emailsKey(insurerId, contactId) }),
  });
}

/**
 * Désactivation d'un email avec remplaçant principal optionnel (9.V.2b) —
 * un SEUL appel, promotion transactionnelle côté serveur.
 */
export function useDeactivateInsurerContactEmail(insurerId: string, contactId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { emailId: string; replacementEmailId?: string | null }) =>
      api.post<{ deactivated: InsurerContactEmailRow; new_primary: InsurerContactEmailRow | null }>(
        `/v1/insurers/${insurerId}/contacts/${contactId}/emails/${args.emailId}/deactivate`,
        args.replacementEmailId ? { replacement_email_id: args.replacementEmailId } : {},
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: emailsKey(insurerId, contactId) }),
  });
}
